import React from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle2,
  Laptop,
  Users,
  FolderKanban,
  Shield,
  Star,
  Quote,
  Target,
  Lightbulb,
  Rocket,
  Globe,
  Code,
  Cloud,
  Palette,
  Building2,
  GraduationCap,
  Heart,
  TrendingUp,
  Award,
  Zap,
} from "lucide-react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { cn } from "../../lib/utils";
import project1 from "../../assets/img/balvatika.jpg";
import face1 from "../../assets/img/faces/face5.jpg";
import face2 from "../../assets/img/faces/face7.jpg";
import face3 from "../../assets/img/faces/face8.jpg";
import face4 from "../../assets/img/faces/face9.jpg";

const AboutOethical = () => {
  const testimonials = [
    {
      img: face1,
      name: "Saksham Shrivastava",
      role: "School Administrator",
      quote:
        "From the moment we implemented EduAdminHub, our institution experienced a seamless transition to a modern school management system. The onboarding process was intuitive, providing all the tools needed to streamline administrative tasks.",
    },
    {
      img: face2,
      name: "Kapil Rai",
      role: "Principal",
      quote:
        "EduAdminHub has transformed the way our school operates. The platform is user-friendly, reliable, and packed with features that simplify attendance tracking, fee management, and communication.",
    },
    {
      img: face3,
      name: "Fahad Masood",
      role: "IT Coordinator",
      quote:
        "As someone working closely with EduAdminHub, I can attest to its excellence as an all-in-one school management solution. The platform is highly flexible, secure, and optimized for performance.",
    },
    {
      img: face4,
      name: "Ankita Khare",
      role: "School Director",
      quote:
        "I highly recommend EduAdminHub by Oethical Technology to any educational institution seeking a robust management system. The platform's intuitive design and exceptional support have streamlined our operations.",
    },
  ];

  const stats = [
    {
      icon: Laptop,
      count: "500+",
      label: "Projects Delivered",
      gradient: "from-blue-500/20 to-cyan-500/20",
    },
    {
      icon: Users,
      count: "200+",
      label: "Global Clients",
      gradient: "from-green-500/20 to-emerald-500/20",
    },
    {
      icon: FolderKanban,
      count: "50+",
      label: "Ongoing Projects",
      gradient: "from-purple-500/20 to-pink-500/20",
    },
    {
      icon: Shield,
      count: "99.9%",
      label: "System Uptime",
      gradient: "from-orange-500/20 to-red-500/20",
    },
  ];

  const features = [
    {
      title: "Proven Track Record",
      description: "Reliable IT services with consistent delivery and client satisfaction.",
      icon: Award,
    },
    {
      title: "Tailored Solutions",
      description: "Custom solutions for education, healthcare, and business sectors.",
      icon: Target,
    },
    {
      title: "Data Security",
      description: "Strong focus on data security, compliance, and privacy protection.",
      icon: Shield,
    },
    {
      title: "24/7 Support",
      description: "Round-the-clock technical support and dedicated account managers.",
      icon: Zap,
    },
  ];

  const services = [
    {
      title: "Web & Mobile Development",
      description: "Custom Web & Mobile App Development",
      icon: Code,
    },
    {
      title: "Enterprise Solutions",
      description: "ERP, CRM, EdTech, and HealthTech Solutions",
      icon: Building2,
    },
    {
      title: "Cloud & DevOps",
      description: "Cloud & DevOps Consulting",
      icon: Cloud,
    },
    {
      title: "Design & Integration",
      description: "UI/UX Design and API Integrations",
      icon: Palette,
    },
  ];

  const expertise = [
    {
      title: "Modern Tech Stack",
      description: "Skilled in React, Angular, Node.js, Flutter, PHP, WordPress",
      icon: Rocket,
    },
    {
      title: "Agile Development",
      description: "Agile development processes and DevOps practices",
      icon: TrendingUp,
    },
    {
      title: "Client-First Approach",
      description: "Transparent communication and client-centric solutions",
      icon: Heart,
    },
  ];

  const journey = [
    { label: "Schools Managed", value: "6+", icon: GraduationCap },
    { label: "Hospitals Using HMS", value: "2+", icon: Heart },
    { label: "Projects Delivered", value: "500+", icon: FolderKanban },
    { label: "Daily Active Users", value: "5000+", icon: Users },
  ];

  const projects = [
    {
      name: "SRIJAN BAL VATIKA",
      description: "Social and Educational Trust",
      image: project1,
      link: "/",
    },
    {
      name: "Government of Madhya Pradesh",
      description: "Department of Information Technology",
      image: "assets/img/OELogoNew.jpg",
      link: "/",
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative py-12 md:py-20 lg:py-24 bg-gradient-to-br from-background via-muted/20 to-background border-b overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05]">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, hsl(var(--foreground)) 1px, transparent 0)`,
            backgroundSize: '50px 50px'
          }} />
        </div>

        <div className="container relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Content */}
            <div className="space-y-6 text-center lg:text-left">
              <div className="inline-block">
                <span className="text-sm font-medium text-primary bg-primary/10 px-4 py-2 rounded-full border border-primary/20">
                  About Us
                </span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                Empowering Businesses through{" "}
                <span className="text-primary bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
                  Innovative IT Solutions
                </span>
              </h1>

              <p className="text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto lg:mx-0">
                At <strong className="text-foreground">Oethical Technology</strong>, we are dedicated to
                redefining the way businesses leverage technology. We help
                organizations achieve efficiency, scalability, and digital
                transformation through our cutting-edge solutions.
              </p>

              <ul className="space-y-3 text-left max-w-xl mx-auto lg:mx-0">
                {features.map((feature, index) => {
                  const Icon = feature.icon;
                  return (
                    <li key={index} className="flex items-start gap-3">
                      <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <CheckCircle2 className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <span className="font-medium text-foreground">{feature.title}</span>
                        <span className="text-muted-foreground"> - {feature.description}</span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Image */}
            <div className="relative order-first lg:order-last">
              <div className="absolute -inset-4 bg-gradient-to-br from-primary/20 to-primary/5 rounded-3xl blur-3xl -z-10" />
              <div className="relative rounded-3xl overflow-hidden border border-border/50 shadow-2xl">
                <img
                  src="assets/img/hero (2).jpg"
                  alt="Oethical Technology"
                  className="w-full h-[400px] md:h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background/60 to-transparent" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 md:py-20 bg-muted/30 border-y">
        <div className="container">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <Card
                  key={index}
                  className={cn(
                    "text-center border-border hover:border-primary/50 transition-all duration-300 hover:shadow-xl group",
                    "bg-gradient-to-br",
                    stat.gradient
                  )}
                >
                  <CardHeader>
                    <div className="h-14 w-14 rounded-xl bg-background/80 backdrop-blur-sm flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform shadow-lg">
                      <Icon className="h-7 w-7 text-primary" />
                    </div>
                    <CardTitle className="text-3xl md:text-4xl font-bold text-primary mb-2">
                      {stat.count}
                    </CardTitle>
                    <CardDescription className="text-sm md:text-base font-medium">
                      {stat.label}
                    </CardDescription>
                  </CardHeader>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Mission and Values */}
      <section className="py-16 md:py-20 bg-background">
        <div className="container">
          <div className="max-w-4xl mx-auto space-y-8">
            <div className="text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Mission and{" "}
                <span className="text-primary">Values</span>
              </h2>
              <div className="w-16 h-1 bg-primary mx-auto rounded-full" />
            </div>

            <p className="text-base md:text-lg text-muted-foreground leading-relaxed text-center">
              At Oethical Technology, our mission is to build impactful and
              sustainable IT solutions. We focus on solving real-world challenges
              through smart, reliable software. From school management systems to
              healthcare platforms, we deliver tailored digital tools. Our goal is
              to empower institutions with technology that enhances efficiency and
              growth. Innovation, reliability, and client success are at the core
              of everything we create.
            </p>

            <div className="grid md:grid-cols-2 gap-6 pt-8">
              {services.map((service, index) => {
                const Icon = service.icon;
                return (
                  <Card
                    key={index}
                    className="border-border hover:border-primary/50 transition-all hover:shadow-lg"
                  >
                    <CardHeader>
                      <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                        <Icon className="h-6 w-6 text-primary" />
                      </div>
                      <CardTitle className="text-xl mb-2">{service.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-base">
                        {service.description}
                      </CardDescription>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Our Expertise */}
      <section className="py-16 md:py-20 bg-muted/30">
        <div className="container">
          <div className="max-w-4xl mx-auto space-y-8">
            <div className="text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Our{" "}
                <span className="text-primary">Expertise</span>
              </h2>
              <div className="w-16 h-1 bg-primary mx-auto rounded-full" />
            </div>

            <p className="text-base md:text-lg text-muted-foreground leading-relaxed text-center">
              We are a skilled team of developers, designers, and domain experts.
              Our focus is on delivering innovative, future-ready IT solutions. We
              specialize in web, mobile, and cloud-based technologies. Agile
              practices and modern tech stacks drive our development process. We
              ensure quality, scalability, and client-centric outcomes in every
              project.
            </p>

            <div className="grid md:grid-cols-3 gap-6 pt-8">
              {expertise.map((item, index) => {
                const Icon = item.icon;
                return (
                  <Card
                    key={index}
                    className="border-border hover:border-primary/50 transition-all hover:shadow-lg text-center"
                  >
                    <CardHeader>
                      <div className="h-14 w-14 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                        <Icon className="h-7 w-7 text-primary" />
                      </div>
                      <CardTitle className="text-xl mb-2">{item.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-base">
                        {item.description}
                      </CardDescription>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Our Journey */}
      <section className="py-16 md:py-20 bg-background">
        <div className="container">
          <div className="max-w-4xl mx-auto space-y-8">
            <div className="text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Our{" "}
                <span className="text-primary">Journey</span>
              </h2>
              <div className="w-16 h-1 bg-primary mx-auto rounded-full" />
            </div>

            <p className="text-base md:text-lg text-muted-foreground leading-relaxed text-center">
              <strong className="text-foreground">Oethical Technology</strong> was established by tech
              visionaries with a shared passion for innovation. Our journey began
              with a mission to transform industries through smart digital
              solutions. We've collaborated with startups, educational
              institutions, and healthcare providers. Through continuous
              innovation, we've helped them scale faster, smarter, and more
              efficiently. Our commitment to progress drives everything we do.
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 pt-8">
              {journey.map((item, index) => {
                const Icon = item.icon;
                return (
                  <Card
                    key={index}
                    className="text-center border-border hover:border-primary/50 transition-all hover:shadow-lg"
                  >
                    <CardHeader>
                      <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mx-auto mb-4">
                        <Icon className="h-6 w-6 text-primary" />
                      </div>
                      <CardTitle className="text-2xl md:text-3xl font-bold text-primary mb-2">
                        {item.value}
                      </CardTitle>
                      <CardDescription className="text-sm md:text-base">
                        {item.label}
                      </CardDescription>
                    </CardHeader>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section className="py-16 md:py-20 bg-muted/30">
        <div className="container">
          <div className="text-center mb-12">
            <div className="inline-block mb-2">
              <span className="text-sm font-medium text-primary bg-primary/10 px-4 py-2 rounded-full">
                Portfolio
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Check Out Our{" "}
              <span className="text-primary">Projects</span>
            </h2>
            <div className="w-16 h-1 bg-primary mx-auto rounded-full" />
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {projects.map((project, index) => (
              <Card
                key={index}
                className="border-border hover:border-primary/50 transition-all hover:shadow-xl overflow-hidden group"
              >
                <div className="relative h-48 overflow-hidden bg-muted">
                  <img
                    src={project.image}
                    alt={project.name}
                    className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <CardHeader>
                  <CardTitle className="text-xl">{project.name}</CardTitle>
                  <CardDescription className="text-base">
                    {project.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button variant="outline" asChild className="w-full">
                    <Link to={project.link}>Read More</Link>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 md:py-20 bg-background relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-[0.02] dark:opacity-[0.05]">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, hsl(var(--foreground)) 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }} />
        </div>

        <div className="container relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Client{" "}
              <span className="text-primary">Testimonials</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              What our clients say about our trusted, innovative solutions.
            </p>
            <div className="w-16 h-1 bg-primary mx-auto rounded-full mt-4" />
          </div>

          <Swiper
            modules={[Navigation, Pagination, Autoplay]}
            spaceBetween={30}
            slidesPerView={1}
            breakpoints={{
              768: { slidesPerView: 2 },
              1024: { slidesPerView: 3 },
            }}
            autoplay={{ delay: 5000, disableOnInteraction: false }}
            pagination={{ clickable: true }}
            navigation
            className="pb-12"
          >
            {testimonials.map((testimonial, index) => (
              <SwiperSlide key={index}>
                <Card className="h-full border-border hover:border-primary/50 transition-all hover:shadow-xl">
                  <CardHeader>
                    <div className="flex items-center gap-4 mb-4">
                      <div className="relative">
                        <img
                          src={testimonial.img}
                          alt={testimonial.name}
                          className="w-16 h-16 rounded-full object-cover border-2 border-primary/20"
                        />
                        <div className="absolute -bottom-1 -right-1 h-5 w-5 rounded-full bg-primary border-2 border-background" />
                      </div>
                      <div>
                        <CardTitle className="text-lg mb-1">{testimonial.name}</CardTitle>
                        <CardDescription>{testimonial.role}</CardDescription>
                      </div>
                    </div>
                    <div className="flex gap-1 mb-4">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="relative">
                      <Quote className="absolute -top-2 -left-2 h-8 w-8 text-primary/20" />
                      <p className="text-sm md:text-base text-muted-foreground leading-relaxed pl-6">
                        {testimonial.quote}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </section>
    </div>
  );
};

export default AboutOethical;
